import { history } from "@umijs/max";
import { message } from "antd";
import { useCallback, useState } from "react";
import {
  getUserPermissions as getUserPermissionsApi,
  LoginParams,
  LoginResult,
  login as loginApi,
  logout as logoutApi,
} from "@/services/api/user";

export interface UserInfo extends LoginResult {
  // 可以扩展其他用户信息字段
}

export interface UserModel {
  // 状态
  userInfo: UserInfo | null;
  token: string | null;
  permissions: string[];
  menus: any[];
  isLoggedIn: boolean;
  currentPage: number;

  // 方法
  login: (params: LoginParams) => Promise<boolean>;
  logout: () => Promise<void>;
  getUserPermissions: (projectId: number) => Promise<void>;
  setUserInfo: (userInfo: UserInfo | null) => void;
  setToken: (token: string | null) => void;
  setCurrentPage: (page: number) => void;
  clearUserData: () => void;
}

const useUserModel = (): UserModel => {
  // 从 localStorage 恢复数据
  const getStoredData = useCallback(() => {
    try {
      const token = localStorage.getItem("token");
      const userInfo = localStorage.getItem("userInfo");
      const permissions = localStorage.getItem("permissions");
      const menus = localStorage.getItem("menus");

      return {
        token,
        userInfo: userInfo ? JSON.parse(userInfo) : null,
        permissions: permissions ? JSON.parse(permissions) : [],
        menus: menus ? JSON.parse(menus) : [],
      };
    } catch {
      return {
        token: null,
        userInfo: null,
        permissions: [],
        menus: [],
      };
    }
  }, []);

  const storedData = getStoredData();

  const [userInfo, setUserInfoState] = useState<UserInfo | null>(
    storedData.userInfo,
  );
  const [token, setTokenState] = useState<string | null>(storedData.token);
  const [permissions, setPermissions] = useState<string[]>(
    storedData.permissions,
  );
  const [menus, setMenus] = useState<any[]>(storedData.menus);
  const [currentPage, setCurrentPageState] = useState<number>(1);

  const isLoggedIn = !!(token && userInfo);

  // 设置用户信息
  const setUserInfo = useCallback((newUserInfo: UserInfo | null) => {
    setUserInfoState(newUserInfo);
    if (newUserInfo) {
      localStorage.setItem("userInfo", JSON.stringify(newUserInfo));
    } else {
      localStorage.removeItem("userInfo");
    }
  }, []);

  // 设置 token
  const setToken = useCallback((newToken: string | null) => {
    setTokenState(newToken);
    if (newToken) {
      localStorage.setItem("token", newToken);
    } else {
      localStorage.removeItem("token");
    }
  }, []);

  // 登录
  const login = useCallback(async (params: LoginParams): Promise<boolean> => {
    try {
      const response = await loginApi(params);

      if (response.success && response.data) {
        const userInfo = response.data;

        // 存储用户信息和 token
        setUserInfo(userInfo);
        setToken(userInfo.token);

        // 获取用户权限和菜单
        await getUserPermissions(userInfo.projectId);

        message.success(`登录成功，欢迎 ${userInfo.userName}！`);
        return true;
      } else {
        message.error(response.message || "登录失败");
        return false;
      }
    } catch (error: any) {
      console.error("登录失败:", error);
      message.error(error.message || "登录失败，请稍后重试");
      return false;
    }
  }, []);

  // 获取用户权限和菜单
  const getUserPermissions = useCallback(
    async (projectId: number): Promise<void> => {
      try {
        const response = await getUserPermissionsApi(projectId);

        if (response.success && response.data) {
          const { menus, permissions } = response.data;

          setMenus(menus);
          setPermissions(permissions);

          // 存储到 localStorage
          localStorage.setItem("menus", JSON.stringify(menus));
          localStorage.setItem("permissions", JSON.stringify(permissions));

          // 根据菜单跳转到首页
          const defaultRoute = findDefaultRoute(menus);
          console.log("defaultRoute", defaultRoute);
          if (defaultRoute) {
            console.log("准备跳转到:", defaultRoute);
            // 使用延时确保localStorage数据已经保存，然后强制跳转
            window.location.href = defaultRoute;
            setTimeout(() => {
              // history.replace(defaultRoute);
              // window.location.href = defaultRoute;
            }, 100);
          } else {
            history.replace("/");
          }
        }
      } catch (error: any) {
        console.error("获取用户权限失败:", error);
        message.error("获取用户权限失败");
      }
    },
    [],
  );

  // 查找默认路由
  const findDefaultRoute = useCallback((menus: any[]): string => {
    // 查找第一个有 menuPath 的菜单项
    for (const menu of menus) {
      if (menu.menuPath && menu.menuPath !== "" && menu.menuPath !== "/") {
        // return menu.menuPath;
      }
    }
    // 如果没找到，返回根路径
    return "/dashboardWorkOrder";
  }, []);

  // 登出
  const logout = useCallback(async (): Promise<void> => {
    console.log('logout...');
    try {
      // await logoutApi();
      // 模拟调用退出登录接口成功
      await new Promise((resolve) => setTimeout(resolve, 100));
      console.log('退出登录接口调用成功');
    } catch (error) {
      console.error("登出请求失败:", error);
    } finally {
      // 无论接口是否成功，都清除本地数据
      clearUserData();
      // message.success("已退出登录");
      // history.push("/user/login");
    }
  }, []);

  // 清除用户数据
  const clearUserData = useCallback(() => {
    setUserInfoState(null);
    setTokenState(null);
    setPermissions([]);
    setMenus([]);

    // 清除 localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("permissions");
    localStorage.removeItem("menus");
  }, []);

  return {
    // 状态
    userInfo,
    token,
    permissions,
    menus,
    isLoggedIn,
    currentPage,

    // 方法
    login,
    logout,
    getUserPermissions,
    setUserInfo,
    setToken,
    setCurrentPage: setCurrentPageState,
    clearUserData,
  };
};

export default useUserModel;
